/* eslint-disable no-lone-blocks */
import { compactSmall } from "../../helpers/formatters"
import { css, cx } from "@emotion/css"
import { fetchBrand, fetchMarket, fetchRegions } from "../../api"
import { WidgetCard } from "../Widgets/WidgetCard"
import { WidgetScale } from "../Widgets/WidgetScale"
import { WidgetStats } from "../Widgets/WidgetStats"
import { WidgetStatsPie } from "../Widgets/WidgetStatsPie"
import { stylesWidgetStats } from "../../styles"
import { useI18n } from "providers/I18n"
import { useQuery } from "react-query"
import { useRevenueParams } from "../../hooks/useRevenueParams"
import { Loading } from "components/Loading"
import { EconomicalDataBox } from "./EconomicalDataBox"
import { useMemo } from "react"

export function RevenueBox() {
  const { t } = useI18n()
  const { search, regionEnabled, marketEnabled, brandEnabled, regionParam } =
    useRevenueParams()

  // depending on params enable the proper data source

  const viewEnabled = regionEnabled || marketEnabled || brandEnabled

  // regions
  const { data: regions, isLoading: isLoadingRegions } = useQuery(
    ["regions", search],
    fetchRegions,
    {
      enabled: !!search,
    },
  )
  // market
  const { data: market, isLoading: isLoadingMarket } = useQuery(
    ["market", search],
    fetchMarket,
    {
      enabled: !!search,
    },
  )

  // brand
  const { data: brand, isLoading: isLoadingBrand } = useQuery(
    ["brand", search],
    fetchBrand,
    {
      enabled: brandEnabled,
    },
  )

  const data = useMemo(() => {
    switch (viewEnabled) {
      case regionEnabled:
        return regions?.find(
          (d: { name: string }) =>
            d.name.toLowerCase() === regionParam.toLowerCase(),
        )?.economicalData

      case marketEnabled:
        return market?.economicalData

      case brandEnabled:
        return brand?.economicalData

      default:
        return
    }
  }, [
    viewEnabled,
    regionEnabled,
    regions,
    marketEnabled,
    market?.economicalData,
    brandEnabled,
    brand?.economicalData,
    regionParam,
  ])

  const isLoading = isLoadingMarket || isLoadingBrand || isLoadingRegions

  const resetScaleWidget = -100

  return (
    <>
      <div className={styles.containerColumn}>
        {!isLoading ? <EconomicalDataBox data={data} /> : <Loading />}

        <div>
          <WidgetStatsPie
            currency="$"
            current={data?.srs?.current}
            note={t("revenue.srs")}
            percentage={data?.srs?.percentage}
            subnote={t("revenue.ofrevenue")}
            className={css`
              color: var(--primary-main-dark-green);
            `}
            isLoading={isLoading}
            hasNoDataToShow={data?.srs?.percentage === null}
          />
        </div>

        <div>
          <WidgetStatsPie
            currency="$"
            current={data?.operatingExpenses?.current}
            note={t("revenue.operatingexpenses")}
            percentage={data?.operatingExpenses?.percentage}
            subnote={t("revenue.ofrevenue")}
            className={css`
              color: var(--other-yellow);
            `}
            isLoading={isLoading}
            hasNoDataToShow={data?.operatingExpenses?.percentage === null}
          />
        </div>
      </div>
      <div className={styles.widgetsGrid}>
        <div className={cx(stylesWidgetStats.topElement)}>
          <WidgetCard
            currency="$"
            value={data?.tbg.value}
            detail1={t("revenue.tbgrevenue")}
            detail2={
              data?.tbg?.percentage
                ? `${compactSmall(data?.tbg?.percentage)}%  ${t(
                    "revenue.offyrevenue",
                  )}`
                : ""
            }
            className={css`
              background-color: var(--primary-main-extra-dark-green);
            `}
            iconClassName={css`
              background-color: var(--primary-main-extra-dark-2-green);
            `}
            isLoading={isLoading}
          />
        </div>
        <div className={stylesWidgetStats.topElement}>
          <WidgetCard
            currency="$"
            value={data?.margin?.value}
            detail1={t("revenue.oibia")}
            detail2={
              data?.margin?.percentage
                ? `${compactSmall(data?.margin?.percentage)}% ${t(
                    "revenue.oibia",
                  )} ${t("revenue.margin")}`
                : ""
            }
            className={css`
              background-color: var(--primary-main-green);
            `}
            iconClassName={css`
              background-color: var(--primary-main-dark-green);
            `}
            isLoading={isLoading}
          />
        </div>
        <div className={stylesWidgetStats.topElement}>
          <WidgetStats
            value={data?.headcount?.current}
            percentage={data?.headcount?.growth}
            isLoading={isLoading}
          />
        </div>
        <WidgetScale
          value={data?.nps?.value ?? resetScaleWidget}
          percentage={data?.nps?.value ?? resetScaleWidget}
          nps={data?.nps}
          isLoading={isLoading}
          title={t("revenue.nps")}
        />
      </div>
    </>
  )
}

const styles = {
  containerColumn: css`
    display: flex;
    flex-direction: column;
    align-content: center;
    justify-content: space-between;
  `,
  widgetsGrid: css`
    display: grid;
    grid-template-rows: 1fr 1fr;
    grid-template-columns: 1fr 1fr;
    grid-row-gap: var(--spacing-xxs);
    grid-column-gap: var(--spacing-xxl-2);
    margin-top: 18px;
  `,
}
